import { useQueries, useQuery } from '@tanstack/react-query'
import {
  ArrowDownLeft,
  ArrowUpRight,
  Folder,
  FolderDown,
  FolderUp,
  type LucideIcon,
} from 'lucide-react'
import { useMemo } from 'react'
import { useTRPC } from '../../lib/trpc'
import { type FolderTree, folderTree, type TreeFolder } from './folder-tree'

/**
 * Which way a document or folder is shared, said by the arrow's shape: out of
 * the person's own space, or in from somebody else's. The shape carries it, so
 * the label stays plain and the chip stays gray — direction is not a state
 * that asks anything of the reader.
 */
export type Direction = 'out' | 'in'

const DIRECTION: Record<Direction, { label: string; icon: LucideIcon }> = {
  out: { label: 'Shared by you', icon: ArrowUpRight },
  in: { label: 'Shared with you', icon: ArrowDownLeft },
}

/** The icon for a direction, where something names it without the words. */
export const DIRECTION_ICON = {
  out: ArrowUpRight,
  in: ArrowDownLeft,
} as const satisfies Record<Direction, LucideIcon>

/** The words and the arrow, for a card's badge or a table cell. */
export function DirectionLabel({ direction }: { direction: Direction }) {
  const { label, icon: Icon } = DIRECTION[direction]
  return (
    <>
      <Icon size={12} aria-hidden />
      {label}
    </>
  )
}

/**
 * A folder's mark by kind: one of the person's own, one of theirs that is
 * shared out, or one shared with them.
 */
export function folderIcon(kind: Direction | null): LucideIcon {
  return kind === 'in' ? FolderDown : kind === 'out' ? FolderUp : Folder
}

/**
 * Every folder of a space that somebody else reaches: each one shared, and
 * everything beneath it, since a folder share reaches the whole branch.
 */
export function sharedOutFolders(
  tree: FolderTree<TreeFolder & { sharedWith: number }>,
): ReadonlySet<string> {
  const out = new Set<string>()
  for (const { folder } of tree.flat()) {
    if (folder.sharedWith > 0 && !out.has(folder.id))
      for (const id of tree.within(folder.id)) out.add(id)
  }
  return out
}

/**
 * The folders somebody else reaches, across several spaces: each space's
 * folders read, and the shared branches of all of them together. A folder id
 * is one space's, so the union names each folder once.
 */
export function useSharedOutFolders(spaceIds: readonly string[]) {
  const trpc = useTRPC()
  const reads = useQueries({
    queries: spaceIds.map((spaceId) =>
      trpc.folders.list.queryOptions({ spaceId }),
    ),
  })
  const out = new Set<string>()
  reads.forEach((q, i) => {
    const tree = folderTree(
      (q.data ?? []).filter((f) => !f.unfiled),
      spaceIds[i] ?? null,
    )
    for (const id of sharedOutFolders(tree)) out.add(id)
  })
  return { out: out as ReadonlySet<string>, reads }
}

/** Whether a document of the person's own reaches anybody else. */
export function isSharedOut(
  w: { sharedWith: number; liveLinks: number; folderId: string | null },
  outFolders: ReadonlySet<string>,
): boolean {
  return (
    w.sharedWith > 0 ||
    w.liveLinks > 0 ||
    (w.folderId !== null && outFolders.has(w.folderId))
  )
}

/**
 * What other people share with this person, split the way it is shown: a
 * document a shared folder reaches is listed inside that folder and nowhere
 * else, so "Shared with me" holds only what was shared on its own.
 *
 * Neither read is space-scoped, and cannot be: what is shared with you lives
 * in somebody else's space.
 */
export function useSharedWithMe() {
  const trpc = useTRPC()
  const docs = useQuery(trpc.share.sharedWithMe.queryOptions())
  const folders = useQuery(trpc.share.sharedFolders.queryOptions())
  /* Gathered from whoever shares them, so nothing lands here. */
  const tree = useMemo(
    () => folderTree(folders.data ?? [], null),
    [folders.data],
  )
  const alone = useMemo(
    () => (docs.data ?? []).filter((d) => d.folderId === null),
    [docs.data],
  )
  return { docs, folders, tree, alone }
}
